import { oc } from "@orpc/contract";
import { z } from "zod";

/**
 * AR Customer Intelligence RPC Contract
 *
 * Provides endpoints for AI-generated collection intelligence on a customer:
 * an email communication summary, a recommended collection action from a
 * locked taxonomy, and a rationale grounded in customer data.
 *
 * Plan 03-02 implements the AI agent that populates these values.
 * Plan 03-03 implements the frontend panel that displays them.
 */

/**
 * Const-object enum of automation-ready collection actions.
 * Each value maps to a distinct, directly actionable operation.
 * LOCKED: do not add/remove/rename values without updating the AI prompt.
 */
export const ArCollectionAction = {
  SEND_PAYMENT_REMINDER: "send_payment_reminder",
  SCHEDULE_CALL: "schedule_call",
  SEND_FINAL_NOTICE: "send_final_notice",
  SEND_INVOICE_COPY: "send_invoice_copy",
  OFFER_PAYMENT_PLAN: "offer_payment_plan",
  PLACE_ON_CREDIT_HOLD: "place_on_credit_hold",
  ADJUST_CREDIT_LIMIT: "adjust_credit_limit",
  ESCALATE_TO_MANAGER: "escalate_to_manager",
  EMAIL_SALES_PERSON: "email_sales_person",
  ADJUST_INVOICE: "adjust_invoice",
  // Over-payment resolution actions. Only the over-payment recommendation agent
  // emits these; the short-pay and customer-intelligence prompts enumerate their
  // own subset, so they never surface them.
  REFUND_OVERPAYMENT: "refund_overpayment",
  APPLY_CREDIT_TO_ACCOUNT: "apply_credit_to_account",
  ISSUE_CREDIT_MEMO: "issue_credit_memo",
} as const;
export type ArCollectionAction =
  (typeof ArCollectionAction)[keyof typeof ArCollectionAction];

/**
 * The full collection-action taxonomy as a tuple, the single source of truth
 * for every `z.enum(...)` over these values (customer intelligence, short-pay
 * recommendations, dismissals). Add/remove here only — and update the AI prompt
 * per the LOCKED note above.
 */
export const ALL_AR_COLLECTION_ACTIONS = [
  ArCollectionAction.SEND_PAYMENT_REMINDER,
  ArCollectionAction.SCHEDULE_CALL,
  ArCollectionAction.SEND_FINAL_NOTICE,
  ArCollectionAction.SEND_INVOICE_COPY,
  ArCollectionAction.OFFER_PAYMENT_PLAN,
  ArCollectionAction.PLACE_ON_CREDIT_HOLD,
  ArCollectionAction.ADJUST_CREDIT_LIMIT,
  ArCollectionAction.ESCALATE_TO_MANAGER,
  ArCollectionAction.EMAIL_SALES_PERSON,
  ArCollectionAction.ADJUST_INVOICE,
  ArCollectionAction.REFUND_OVERPAYMENT,
  ArCollectionAction.APPLY_CREDIT_TO_ACCOUNT,
  ArCollectionAction.ISSUE_CREDIT_MEMO,
] as const;

/**
 * AI-generated intelligence for a single customer.
 */
export const customerIntelligenceSchema = z.object({
  /** 2-4 sentence factual summary of email communication in the last 14 days,
   *  or "No email communication found in the last 14 days" */
  emailSummary: z.string(),
  /** Single highest-priority action from the allowed taxonomy */
  recommendedAction: z.enum(ALL_AR_COLLECTION_ACTIONS),
  /** 1-2 sentences explaining why this action is recommended,
   *  grounded in specific customer data */
  rationale: z.string(),
});
export type CustomerIntelligence = z.infer<typeof customerIntelligenceSchema>;

/**
 * Full API response schema for customer intelligence endpoints.
 */
export const ArCustomerIntelligenceResponseSchema = z.object({
  /** Cached intelligence result; null when no analysis exists yet */
  intelligence: customerIntelligenceSchema.nullable(),
  /** ISO datetime string of when intelligence was last generated; null if never */
  generatedAt: z.string().nullable(),
  /** true when the customer's data fingerprint has changed since last generation */
  isStale: z.boolean(),
  /** false when no analysis has ever been run for this customer */
  hasCachedResult: z.boolean(),
});

export type ArCustomerIntelligenceResponse = z.infer<
  typeof ArCustomerIntelligenceResponseSchema
>;

/**
 * AR Customer Intelligence Contracts
 */
export const arCustomerIntelligenceContracts = {
  /** Retrieve cached intelligence for a customer (no AI call) */
  get: oc
    .input(z.object({ customerId: z.string().uuid() }))
    .output(ArCustomerIntelligenceResponseSchema)
    .route({ method: "GET" }),
  /** Trigger a fresh AI analysis for a customer */
  regenerate: oc
    .input(z.object({ customerId: z.string().uuid() }))
    .output(ArCustomerIntelligenceResponseSchema)
    .route({ method: "POST" }),
} as const;
